'use client';

import { c as _c } from "react/compiler-runtime";
import OptimizedImage from '@/components/OptimizedImage';
import { Link } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';
import FollowButton from '@/components/FollowButton';
import { FollowDialogType } from '@/components/FollowDialog';
import { LoadingDots } from '@/components/LoadingDots';
import { UseFollowDataFnType, UseFollowDataReturnType } from '@/hooks/useFollowData';
import { cn } from '@/lib/utils';
type FollowDataList = UseFollowDataReturnType['data']['followings'] | UseFollowDataReturnType['data']['followers'];
type FollowDialogContentProps = UseFollowDataFnType & {
  followDataList: FollowDataList;
  type: FollowDialogType;
  className: string;
};
function FollowDialogContent(t0) {
  const $ = _c(32);
  let className;
  let followDataList;
  let props;
  let type;
  if ($[0] !== t0) {
    ({
      followDataList,
      type,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = followDataList;
    $[3] = props;
    $[4] = type;
  } else {
    className = $[1];
    followDataList = $[2];
    props = $[3];
    type = $[4];
  }
  const t = useTranslations();
  let t1;
  if ($[5] !== t) {
    t1 = t("profile-page.intro-dialog-empty-falback-text");
    $[5] = t;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t) {
    t2 = t("profile-page.intro-dialog-empty-fallback-text-followers");
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = {
      followings: t1,
      followers: t2
    };
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const DIALOG_EMPTY_TEXT = t3;
  const {
    setFollowingsSize,
    setFollowersSize,
    reachedFollowingsEnd,
    reachedFollowersEnd,
    isFollowingsValidating,
    isFollowersValidating
  } = props;
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      threshold: 0.1,
      rootMargin: "100px"
    };
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const {
    ref: lastCommentRef,
    isIntersecting
  } = useIntersectionObserver(t4);
  const setSize = type === "followings" ? setFollowingsSize : setFollowersSize;
  const reachedEnd = type === "followings" ? reachedFollowingsEnd : reachedFollowersEnd;
  const isValidating = type === "followings" ? isFollowingsValidating : isFollowersValidating;
  let t5;
  let t6;
  if ($[13] !== isIntersecting || $[14] !== reachedEnd || $[15] !== setSize) {
    t5 = () => {
      if (isIntersecting && !reachedEnd) {
        setSize(_temp);
      }
    };
    t6 = [isIntersecting, setSize, reachedEnd];
    $[13] = isIntersecting;
    $[14] = reachedEnd;
    $[15] = setSize;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  useEffect(t5, t6);
  let t7;
  if ($[18] !== className) {
    t7 = cn("grid w-[calc(100%-48px)] gap-[20px] pb-5", className);
    $[18] = className;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== DIALOG_EMPTY_TEXT || $[21] !== followDataList || $[22] !== lastCommentRef || $[23] !== props || $[24] !== type) {
    t8 = followDataList && followDataList.length > 0 ? followDataList.map((followData, index, array) => <div key={`followData-${followData.preferredUsername}`} className="1.5xl:h-[48px] flex h-[40px] w-full items-center justify-between" {...index === array.length - 1 && {
      ref: lastCommentRef
    }}><Link href={`/profile/${followData.preferredUsername}`} className="1.5xl:gap-[16px] flex items-center gap-[8px] overflow-hidden">{followData?.headIconUrl && <OptimizedImage src={followData?.headIconUrl} width={100} height={100} alt="user snapshot" className="1.5xl:size-[48px] size-[40px] rounded-full" />}<span className="subheader-3 1.5xl:subheader-1 max-w-[100px] truncate pr-[8px] md:max-w-[200px]">{followData.name}</span></Link>{type == "followings" && <FollowButton {...props} following={followData} />}</div>) : <div className="mb-5 flex h-[118px] items-center"><div className="body-1 w-full text-center">{DIALOG_EMPTY_TEXT[type]}</div></div>;
    $[20] = DIALOG_EMPTY_TEXT;
    $[21] = followDataList;
    $[22] = lastCommentRef;
    $[23] = props;
    $[24] = type;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== isValidating) {
    t9 = isValidating && <LoadingDots size="md" />;
    $[26] = isValidating;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t10 = <div className={t7}>{t8}{t9}</div>;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}
function _temp(prev) {
  return prev + 1;
}
export default FollowDialogContent;